import { createDomain, sample } from 'effector'
import { z } from 'zod'
import {
  AxiosErrorType,
  Car,
  CarOptionForRentalContract,
  RentalContract,
} from '~/shared/api'
import {
  FILTER_WITHOUT_EMPTY_ENTITIES,
  Option,
  OptionEnum,
} from '~/shared/config/constants'
import {
  CarStatusEnum,
  DayEnum,
  RentalContractTypeEnum,
  RentalContractWorkScheduleEnum,
} from '~/shared/config/enums'
import { mapMessageErrors } from '~/shared/lib/mapMessageErrors'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { optionSchema, priceRequiredWithZeroScheme } from '~/shared/lib/schemas'

const domain = createDomain('entities.rentalContract.form')

export const formSchema = RentalContract.schema
  .pick({
    carOption: true,
    firstDayIsFree: true,
    workSchedule: true,
    contractType: true,
    contractNumber: true,
    driverId: true,
    dayOff: true,
    rentalServicesOptions: true,
    bookedUntil: true,
  })
  .extend({
    rentPerDay: priceRequiredWithZeroScheme,
    quantityRentalPayments: optionSchema,
  })

export type FormValues = Omit<
  z.infer<typeof formSchema>,
  | 'carOption'
  | 'workSchedule'
  | 'dayOff'
  | 'contractType'
  | 'rentalServicesOptions'
  | 'bookedUntil'
  | 'quantityRentalPayments'
  | 'promotion'
> & {
  carOption: UniqueId | CarOptionForRentalContract | null
  brand?: string
  model?: string
  rental?: string
  terminationDate?: string
  signedAt?: string | null
  responsibleManager?: string
  workSchedule:
    | OptionEnum<RentalContractWorkScheduleEnum>
    | RentalContractWorkScheduleEnum
    | null
  dayOff: OptionEnum<DayEnum> | DayEnum | null
  contractType?:
    | OptionEnum<RentalContractTypeEnum>
    | RentalContractTypeEnum
    | null
  rentalServicesOptions: UniqueId[] | Option[]
  rentPerDay?: number
  workRule?: string
  bookedUntil: string
  quantityRentalPayments: Option | null
  promotion: {
    discount_percent?: number
    discount_amount?: number
    duration_days?: number
  } | null
}

export const fetchCarOptions = async (search?: string) => {
  const builder = Car.where(
    'plateNumber',
    search || FILTER_WITHOUT_EMPTY_ENTITIES,
  )
    .with('carModel')
    .with('carModel.carBrand')
    .with('carRental')
    .with('workRule')
    .where('status', CarStatusEnum.FREE)

  const response = await builder.get(1)
  return response.getData().map((o) => o.getOptionForRentalContract())
}

export const calculateRentPerDay = domain.createEvent<{
  carId?: UniqueId
  workRuleId?: UniqueId
  workSchedule?: RentalContractWorkScheduleEnum
  contractType?: RentalContractTypeEnum
  fn: (value?: number) => void
}>()

export const calculateRentPerDayFx = domain.createEffect<
  {
    carId?: UniqueId
    workRuleId?: UniqueId
    workSchedule?: RentalContractWorkScheduleEnum
    contractType?: RentalContractTypeEnum
    fn: (value?: number) => void
  },
  void,
  AxiosErrorType
>({
  handler: async ({ carId, workRuleId, workSchedule, contractType, fn }) => {
    if (!carId || !workRuleId || !workSchedule) {
      fn(undefined)
      return
    }
    const { rentPerDay } = await RentalContract.getRentPerDay(
      carId,
      workRuleId,
      workSchedule,
      contractType,
    )
    fn(rentPerDay)
  },
})

sample({
  clock: calculateRentPerDay,
  target: calculateRentPerDayFx,
})

sample({
  clock: calculateRentPerDayFx.failData,
  fn(e) {
    return {
      message: mapMessageErrors(e),
      variant: 'error' as const,
    }
  },
  target: snackbarEnqueued,
})
